import { ClickableItem, Flex, Input, Label, Progress, Text, Title, Toggle } from '@upsales/components';
import { FieldTranslationsProvider } from '../FieldTranslations/FieldTranslationsContext';
import { Card, Table, TableColumn, TableHeader, TableRow } from '@upsales/components';
import FieldTranslation from 'App/resources/Model/FieldTranslation';
import CustomerLanguage from 'App/resources/Model/CustomerLanguage';
import { useTranslation } from 'Components/Helpers/translate';
import LanguageSelect from '../Inputs/Selects/LanguageSelect';
import InlineConfirm from 'Components/Dialogs/InlineConfirm';
import { DefaultButton } from '@upsales/components/Buttons';
import React, { useEffect, useState, useRef } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';

type CustomFieldTooltipProps = {
	onChange: (state: FieldTranslation[], translationsToDelete?: number[], tagId?: number) => void;
	translations: (FieldTranslation & { uuid: number })[];
	availableLanguages: CustomerLanguage[];
	subType: string;
	reloadModalPosition?: () => void;
};

const CustomFieldTooltip = ({
	onChange,
	translations,
	availableLanguages,
	subType,
	reloadModalPosition
}: CustomFieldTooltipProps) => {
	const MAX_TOOLTIP_LENGTH = 100;
	const [isShowing, setIsShowing] = useState<boolean>(false);
	const titleRef = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const tagId = translations?.find(lang => lang.tagId)?.tagId;
	const takenLangs = translations.map(lang => lang.language);
	const availSelectablelangs = availableLanguages
		.filter(lang => !lang.isMaster && !takenLangs.includes(lang.language))
		.map(l => l.language);
	const defaultLanguage = availableLanguages.find(defaultLang => defaultLang.isMaster)?.language || '';

	const getNewTranslation = (language?: string) => ({
		uuid: translations.length + 1,
		tagId: tagId || undefined,
		tag: { type: 'customfieldtooltip', subType },
		subType,
		type: 'customfieldtooltip',
		language: language,
		value: '',
		usedCount: 0
	});

	const defaultRow = translations.find(row => row.language === defaultLanguage);
	useEffect(() => {
		if (defaultLanguage && isShowing && !defaultRow) {
			onChange([...translations, getNewTranslation(defaultLanguage)]);
		}
	}, [translations.length, defaultLanguage, isShowing, defaultRow]);

	useEffect(() => {
		if (translations?.length) {
			setIsShowing(true);
		}
	}, [translations?.length ?? 0]);

	const usedLangs = translations.map(lang => lang.language);
	const filteredLangs = availableLanguages.filter(lang => !usedLangs.includes(lang.language));
	const unUsedLangs = filteredLangs.map(lang => lang.language.substring(0, 2));
	const canAddLang = unUsedLangs.length > 0;

	const onAddTranslation = () => {
		const newState = [...translations, getNewTranslation()];
		onChange(newState, [], tagId);
	};

	const onChangeTranslationValue = (uuid: number, value: string) => {
		const newState = translations.map(row => (row.uuid === uuid ? { ...row, value } : row));
		onChange(newState, [], tagId);
	};

	const onChangeTranslationLanguage = (uuid: number, language: string) => {
		const newState = translations.map(row => (row.uuid === uuid ? { ...row, language } : row));
		onChange(newState, [], tagId);
	};

	const onDelete = (row: CustomFieldTooltipProps['translations'][number]) => {
		const newState = translations.filter(translation => translation.uuid !== row.uuid);
		const rowToDelete = translations.find(translation => translation.id === row.id);

		onChange(newState, rowToDelete?.id ? [rowToDelete.id] : [], tagId);
		return newState;
	};

	const deleteAllTranslations = () => {
		onChange([], translations.map(row => row.id).filter(Boolean) as number[], tagId);
	};

	const onToggle = (checked: boolean) => {
		if (!checked) {
			deleteAllTranslations();
		}
		if (checked && titleRef.current) {
			setTimeout(() => {
				titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}, 0);
		}
		setTimeout(() => {
			reloadModalPosition?.();
		}, 0);
		setIsShowing(checked);
	};

	const lang = {
		cardTitle: t('admin.customFields.tooltip.cardTitle'),
		cardSubTitle: t('admin.customFields.tooltip.cardSubTitle'),
		label: t('admin.customFields.tooltip.label'),
		fieldPlaceholder: t('admin.customFields.tooltip.fieldPlaceholder'),
		translations: t('admin.customFields.tooltip.translations'),
		language: t('admin.customFields.tooltip.language'),
		translation: t('admin.customFields.tooltip.translation'),
		addTranslation: t('admin.customFields.tooltip.addTranslation'),
		translationPlaceholder: t('admin.customFields.tooltip.translationPlaceholder'),
		rowPlaceholder: t('admin.customFields.tooltip.rowPlaceholder'),
		deleteConfirmTitle: t('admin.customFields.tooltip.deleteConfirmTitle')
	};

	const className = new BemClass('CustomFieldTooltip');

	return (
		<FieldTranslationsProvider types={['customfieldtooltip']}>
			<Card className={className.b()} space="mtxl ptl mbxl" borderRadius>
				<Flex direction="row" alignItems="center" space="mll pbl">
					<Toggle onChange={checked => onToggle(checked)} checked={isShowing} />
					<Flex direction="column" space="mll">
						<Title color="black" size="md" ref={titleRef}>
							{lang.cardTitle}
						</Title>
						{!isShowing ? <Text color="grey-11">{lang.cardSubTitle}</Text> : null}
					</Flex>
				</Flex>

				{isShowing ? (
					<>
						<Flex direction="column" space="mll mrl mbs mtm">
							<Flex direction="row" alignItems="center" justifyContent="space-between">
								<Label>{lang.label}</Label>
								<Progress
									percentage={
										((defaultRow?.value?.toString?.().length ?? 0) / (MAX_TOOLTIP_LENGTH - 1)) * 100
									}
									hideText
								/>
							</Flex>
							<Input
								state={defaultRow?.value ? null : 'warning'}
								placeholder={lang.fieldPlaceholder}
								value={defaultRow?.value ?? ''}
								onChange={e => onChangeTranslationValue(defaultRow?.uuid ?? 0, e.target.value)}
								maxLength={MAX_TOOLTIP_LENGTH}
							/>
							<Flex justifyContent="space-between" alignItems="center" space="mtxl">
								<Text size="lg" space="mtm mbm">
									{lang.translations}
								</Text>

								{canAddLang ? (
									<DefaultButton onClick={onAddTranslation}>{lang.addTranslation}</DefaultButton>
								) : null}
							</Flex>
						</Flex>
						<Table>
							<TableHeader
								columns={[{ title: lang.language }, { title: lang.translation }, { title: '' }]}
							/>
							{translations.filter(row => row.language !== defaultLanguage).length === 0 ? (
								<TableRow>
									<TableColumn size="lg" colSpan={2}>
										<Text color="grey-11" size="sm" italic>
											{lang.rowPlaceholder}
										</Text>
									</TableColumn>
								</TableRow>
							) : (
								translations
									.filter(row => row.language !== defaultLanguage)
									.map(row => (
										<TableRow key={row.uuid}>
											<TableColumn size="lg">
												<Flex gap={10}>
													<LanguageSelect
														autoFocus={row.language === ''}
														value={row.language ?? null}
														availableLanguagesByCode={[
															...availSelectablelangs,
															row.language || ''
														]}
														onChange={selected => {
															if (selected) {
																onChangeTranslationLanguage(
																	row.uuid,
																	String(selected.id)
																);
															}
														}}
														anchor=".CustomFieldTooltip"
														required
													/>
												</Flex>
											</TableColumn>
											<TableColumn>
												<Input
													inline
													placeholder={lang.translationPlaceholder}
													value={row?.value}
													size="md"
													onChange={e => onChangeTranslationValue(row.uuid, e.target.value)}
													maxLength={MAX_TOOLTIP_LENGTH}
												/>
											</TableColumn>
											<TableColumn align="right">
												<Flex
													direction="row"
													alignItems="center"
													justifyContent="flex-end"
													gap={10}
												>
													<Progress
														percentage={
															(row.value.toString().length / MAX_TOOLTIP_LENGTH) * 100
														}
														hideText
													/>
													<InlineConfirm
														show
														titleText={lang.deleteConfirmTitle}
														onConfirm={async () => onDelete(row)}
														dropdownPosition="top"
													>
														<ClickableItem icon="trash" />
													</InlineConfirm>
												</Flex>
											</TableColumn>
										</TableRow>
									))
							)}
						</Table>
					</>
				) : null}
			</Card>
		</FieldTranslationsProvider>
	);
};

export default CustomFieldTooltip;
